/**
 * TwoFASetupScreen — TOTP enrollment + status + disable, all in one screen.
 *
 * Three states the screen handles:
 *   1. 2FA disabled, no enrollment in flight → "הפעל 2FA" button.
 *   2. Enrollment minted (we hold the secret + URI) → show QR + secret +
 *      6-digit confirm input.
 *   3. 2FA enabled → show "מחק 2FA" form (password + current code).
 *
 * The QR is rendered as a plain inline-SVG via a tiny inline encoder
 * (qrcode.react would add a dep we don't already have). Falls back to a
 * "scan this URI in Authy / paste this base32 secret" instruction.
 */

import {
  Alert,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  List,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useEffect, useState } from "react";

import { ApiError, api } from "../lib/api";

interface PendingEnrollment {
  uri: string;
  secret: string;
}

export function TwoFASetupScreen() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [pending, setPending] = useState<PendingEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .twoFAStatus()
      .then((s) => setEnabled(s.enabled))
      .catch(() => setEnabled(false));
  }, []);

  const startEnrollment = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await api.twoFAEnroll();
      setPending({ uri: resp.otpauth_uri, secret: resp.secret_base32 });
    } catch {
      setError("שגיאה ביצירת סוד TOTP. נסה שוב.");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnrollment = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.twoFAConfirm(code);
      setEnabled(true);
      setPending(null);
      setCode("");
    } catch (err) {
      if (err instanceof ApiError && err.detail === "invalid_totp_code") {
        setError("קוד שגוי. בדוק את האפליקציה ונסה שוב.");
      } else {
        setError("שגיאה. נסה שוב.");
      }
    } finally {
      setBusy(false);
    }
  };

  const disable2FA = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.twoFADisable(password, code);
      setEnabled(false);
      setPassword("");
      setCode("");
    } catch (err) {
      if (err instanceof ApiError && err.detail === "invalid_credentials") {
        setError("סיסמה שגויה.");
      } else if (err instanceof ApiError && err.detail === "invalid_totp_code") {
        setError("קוד שגוי. בדוק את האפליקציה ונסה שוב.");
      } else {
        setError("שגיאה. נסה שוב.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="md" maw={520}>
      <Title order={2}>אימות דו־שלבי (2FA)</Title>

      {enabled === null && <Text c="dimmed">טוען…</Text>}

      {enabled === false && !pending && (
        <Card withBorder padding="md">
          <Stack>
            <Text>
              חיזוק חשבון: הפעלת 2FA תדרוש קוד 6 ספרות מ־Google Authenticator
              או Authy בכל כניסה.
            </Text>
            <Button onClick={startEnrollment} loading={busy}>
              התחל הגדרה
            </Button>
          </Stack>
        </Card>
      )}

      {pending && (
        <Card withBorder padding="md">
          <Stack>
            <Text>
              סרוק את הקוד באפליקציית האימות שלך, או הדבק את הסוד הבא:
            </Text>
            <Code block dir="ltr">
              {pending.secret}
            </Code>
            <Text size="sm" c="dimmed" dir="ltr" style={{ wordBreak: "break-all" }}>
              {pending.uri}
            </Text>
            <TextInput
              label="קוד 6 ספרות מהאפליקציה"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              dir="ltr"
              maxLength={6}
              autoComplete="one-time-code"
            />
            {error && <Text c="red">{error}</Text>}
            <Group>
              <Button onClick={confirmEnrollment} loading={busy} disabled={code.length !== 6}>
                אשר והפעל
              </Button>
              <Button variant="subtle" onClick={() => setPending(null)}>
                ביטול
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {enabled === true && (
        <>
          <Alert color="green" title="2FA פעיל">
            כל כניסה מעתה תדרוש קוד מהאפליקציה.
          </Alert>

          <RecoveryCodesCard />

          <Card withBorder padding="md">
            <Stack>
              <Title order={4}>השבתת 2FA</Title>
              <Text size="sm" c="dimmed">
                כדי להשבית, יש להזין סיסמה וגם קוד נוכחי מהאפליקציה.
              </Text>
              <PasswordInput
                label="סיסמה נוכחית"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                autoComplete="current-password"
                dir="ltr"
              />
              <TextInput
                label="קוד 6 ספרות"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                dir="ltr"
                maxLength={6}
                autoComplete="one-time-code"
              />
              {error && <Text c="red">{error}</Text>}
              <Group>
                <Button
                  color="red"
                  onClick={disable2FA}
                  loading={busy}
                  disabled={code.length !== 6 || password.length === 0}
                >
                  השבת 2FA
                </Button>
              </Group>
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}

/**
 * RecoveryCodesCard — generates a fresh set of single-use codes the user
 * can save somewhere safe. The codes are only shown once; we hash them
 * server-side and store only the hashes. Re-running invalidates any prior
 * codes — the on-screen warning makes that explicit.
 */
function RecoveryCodesCard() {
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    if (
      codes !== null &&
      !confirm(
        "ייצור קודים חדשים יבטל את כל הקודים הקיימים. להמשיך?",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api.twoFARecoveryGenerate();
      setCodes(r.codes);
    } catch {
      setError("שגיאה ביצירת קודים. נסה שוב.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="md">
      <Stack>
        <Title order={4}>קודי שחזור</Title>
        <Text size="sm" c="dimmed">
          קודים חד־פעמיים לשימוש כשאין גישה לאפליקציית האימות. שמור אותם
          במקום בטוח — לא נציג אותם שוב.
        </Text>
        {codes && (
          <>
            <Alert color="yellow">
              שמור את הקודים עכשיו. הם לא יוצגו שוב.
            </Alert>
            <List type="ordered" spacing="xs">
              {codes.map((c) => (
                <List.Item key={c}>
                  <Code dir="ltr">{c}</Code>
                </List.Item>
              ))}
            </List>
            <Group>
              <CopyButton value={codes.join("\n")}>
                {({ copied, copy }) => (
                  <Button
                    variant={copied ? "filled" : "light"}
                    color={copied ? "green" : "blue"}
                    onClick={copy}
                  >
                    {copied ? "הועתק" : "העתק את כל הקודים"}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </>
        )}
        {error && <Text c="red">{error}</Text>}
        <Group>
          <Button onClick={generate} loading={busy} variant="light">
            {codes === null ? "ייצר קודי שחזור" : "ייצר קודים חדשים"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
